import moment from 'moment';
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

import { computeAxisCap } from './computeAxisCap';
import type { Cadence, MetricSeriesPoint } from './metricLens';

// recharts wrapper for the single Account Overview activity chart (ENG-5256).
// One metric, custom value labels (inside tall bars / above short ones),
// ~44px max bar, 0/50/100% gridlines, graceful null bars (e.g. Avg Rating's
// empty buckets), and a non-visual alternative (visually-hidden data table)
// alongside recharts' accessibilityLayer so the data is reachable without sight
// or a mouse. Wrapper styling uses Tailwind tokens; only recharts SVG props use
// raw values.
//
// Dense ranges (> 24 buckets, e.g. 30 daily bars) scroll horizontally instead of
// cramming bars: each column keeps a fixed minimum width inside an always-
// scrollbar container, defaulting to the far right (newest bar) on mount. At
// ≤ 24 buckets the chart stays responsive full-width.

// Above this many buckets the chart switches from responsive to fixed-width scroll.
const SCROLL_BUCKET_THRESHOLD = 24;
// Minimum width per column when scrolling, so bars + labels stay readable.
const SCROLL_COLUMN_WIDTH = 36;

interface MetricBarChartProps {
  /** Series points in bucket order. `value: null` renders a gap, not a 0 bar. */
  data: MetricSeriesPoint[];
  /** Chart title — echoes the selected card label verbatim. */
  title: string;
  /** Unit phrase for the accessible description (e.g. "credits spent"). */
  unit: string;
  /** Bucket cadence, drives x-axis label formatting. */
  cadence: Cadence;
  /** Avg Rating uses a fixed 0–5 domain + 1-decimal labels. */
  rating?: boolean;
  /**
   * Decimal places for bar/axis/table values (ENG-5289): credits pass 1 so a
   * 0.7-credit month renders 0.7 instead of rounding to 1 (or vanishing to 0).
   * Whole values stay bare ("2", not "2.0"), matching the card big-value.
   */
  decimals?: 0 | 1;
  height?: number;
}

interface ChartRow extends MetricSeriesPoint {
  label: string;
}

interface BarLabelProps {
  x?: string | number;
  y?: string | number;
  width?: string | number;
  height?: string | number;
  value?: number | string | null;
}

function toNumber(value: string | number | undefined): number | undefined {
  if (value === undefined) return undefined;
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : undefined;
}

const LABEL_INSIDE_MIN_HEIGHT = 24;

function formatBucketLabel(iso: string, cadence: Cadence): string {
  const m = moment(iso);
  return cadence === 'monthly' ? m.format("MMM 'YY") : m.format('MMM D');
}

function formatValue(value: number, rating: boolean, decimals: 0 | 1): string {
  if (rating) return value.toFixed(1);
  if (decimals === 1) return `${Math.round(value * 10) / 10}`;
  return `${Math.round(value)}`;
}

export function MetricBarChart({
  data,
  title,
  unit,
  cadence,
  rating = false,
  decimals = 0,
  height = 200,
}: MetricBarChartProps): React.ReactNode {
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrolls = data.length > SCROLL_BUCKET_THRESHOLD;

  // Buckets are chronological ascending (oldest → newest), so the newest bar is
  // rightmost. Default the scroll position to the far right on mount and whenever
  // the series changes, so the most recent activity is what's shown first.
  // useLayoutEffect avoids a visible jump from left to right after paint.
  useLayoutEffect(() => {
    const node = scrollRef.current;
    if (!scrolls || !node) return;
    node.scrollLeft = node.scrollWidth;
  }, [scrolls, data]);

  // jsdom reports scrollWidth as 0, so the layout effect above is a no-op in
  // tests; this fallback re-applies once geometry is available in the browser.
  useEffect(() => {
    const node = scrollRef.current;
    if (!scrolls || !node) return;
    node.scrollLeft = node.scrollWidth;
  }, [scrolls, data]);

  const rows: ChartRow[] = data.map((point) => ({
    ...point,
    label: formatBucketLabel(point.periodStart, cadence),
  }));

  const values = data.map((p) => p.value).filter((v): v is number => v !== null);
  const total = values.reduce((sum, v) => sum + v, 0);
  const dataMax = Math.max(1, ...values);

  // Outlier cap: when one bar dwarfs the rest, cap the axis just above the
  // tallest non-spike bar so the everyday bars stay readable. Spikes overrun the
  // top (recharts clamps them to the domain max) and keep their TRUE-value label.
  // Avg Rating's 0–5 domain never triggers this, so it's gated behind `!rating`.
  const cap = rating
    ? { capped: false, ceiling: 0, threshold: 0 }
    : computeAxisCap(data.map((p) => p.value));
  const maxValue = rating ? 5 : cap.capped ? cap.ceiling : dataMax;
  // 0 / 50 / 100% ticks of the axis ceiling.
  const ticks = [0, maxValue / 2, maxValue];

  const summary = rating
    ? `${title}: average ${
        values.length ? (total / values.length).toFixed(1) : '—'
      } ${unit} across ${rows.length} ${cadence === 'monthly' ? 'months' : cadence === 'weekly' ? 'weeks' : 'days'}.`
    : `${title}: ${decimals === 1 ? Math.round(total * 10) / 10 : total} ${unit} across ${rows.length} ${
        cadence === 'monthly' ? 'months' : cadence === 'weekly' ? 'weeks' : 'days'
      }.`;

  function renderBarLabel(props: BarLabelProps): React.ReactElement | null {
    const value = typeof props.value === 'string' ? parseFloat(props.value) : props.value;
    const x = toNumber(props.x);
    const y = toNumber(props.y);
    const width = toNumber(props.width);
    const barHeight = toNumber(props.height);
    if (
      value === null ||
      value === undefined ||
      Number.isNaN(value) ||
      x === undefined ||
      y === undefined ||
      width === undefined ||
      barHeight === undefined
    ) {
      return null;
    }

    const text = formatValue(value, rating, decimals);
    const centerX = x + width / 2;
    const fitsInside = barHeight >= LABEL_INSIDE_MIN_HEIGHT;
    // Inside tall bars (white, near the top); above short bars (muted ink).
    const labelY = fitsInside ? y + 14 : y - 6;

    return (
      <text
        x={centerX}
        y={labelY}
        textAnchor="middle"
        className={fitsInside ? 'fill-surface-card' : 'fill-on-surface'}
        fontSize={11}
      >
        {text}
      </text>
    );
  }

  if (data.length === 0) {
    return (
      <figure role="img" aria-label={`${title}: no activity in this range.`}>
        <div className="flex items-center justify-center py-40 text-12 text-on-surface-subtle">
          No activity in this range.
        </div>
      </figure>
    );
  }

  // Shared chart body. `width` is a number for the fixed-width scroll layout and
  // "100%" inside ResponsiveContainer; XAxis drops the preserveStartEnd thinning
  // when scrolling so every dense bucket gets its own visible tick.
  const chartBody = (width: number | string): React.ReactElement => (
    <BarChart
      accessibilityLayer
      data={rows}
      width={typeof width === 'number' ? width : undefined}
      height={typeof width === 'number' ? height : undefined}
      margin={{ top: 16, right: 4, bottom: 0, left: -8 }}
    >
      <CartesianGrid vertical={false} stroke="var(--surfaceBorder)" strokeOpacity={0.6} />
      <XAxis
        dataKey="label"
        interval={scrolls ? 0 : 'preserveStartEnd'}
        minTickGap={scrolls ? 0 : 20}
        tickLine={false}
        axisLine={false}
        tick={{ fontSize: 10, fill: 'var(--onSurfaceMuted)' }}
      />
      <YAxis
        width={36}
        domain={[0, maxValue]}
        ticks={ticks}
        tickFormatter={(v: number) => formatValue(v, rating, decimals)}
        tickLine={false}
        axisLine={false}
        tick={{ fontSize: 10, fill: 'var(--onSurfaceMuted)' }}
      />
      <Bar
        dataKey="value"
        name={title}
        fill="var(--brandPrimary)"
        radius={[3, 3, 0, 0]}
        maxBarSize={44}
        isAnimationActive={false}
      >
        <LabelList dataKey="value" content={renderBarLabel} />
      </Bar>
    </BarChart>
  );

  return (
    <figure className="m-[0]" role="group" aria-label={summary}>
      {scrolls ? (
        <div ref={scrollRef} className="overflow-x-auto scrollbar-thin">
          {chartBody(data.length * SCROLL_COLUMN_WIDTH)}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          {chartBody('100%')}
        </ResponsiveContainer>
      )}

      {/* Non-visual alternative: a visually-hidden table mirroring the bars so
          the data is reachable by screen readers and without a mouse. */}
      <figcaption className="sr-only">{summary}</figcaption>
      <table className="sr-only">
        <caption>{title} by period</caption>
        <thead>
          <tr>
            <th scope="col">Period</th>
            <th scope="col">{unit}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.periodStart}>
              <th scope="row">{row.label}</th>
              <td>{row.value === null ? 'No data' : formatValue(row.value, rating, decimals)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
